import React from "react";
import classes from "./searchSummaryBanner.module.scss";
import {
    QueryPart,
    removeQueryPart,
    SearchSummary,
    summaryHasContent,
    understoodPartCount,
} from "../constants/searchCategories";

interface SearchSummaryBannerProps {
    summary: SearchSummary | null | undefined;
    /** Tighter spacing for the header dropdown and the palette. */
    compact?: boolean;
    /**
     * Run another query: the "Try:" buttons, and the × on an understood chip
     * (which drops that part). Without it neither is shown.
     */
    onPick?: (text: string) => void;
    /** Open a nearest-coverage provider. Without it the rows are plain links opening in a new tab. */
    onOpen?: (url: string) => void;
}

/**
 * What the search understood and what came back — shown above the results on
 * every surface so a list of near misses is never mistaken for the answer:
 *
 *   Understood: [Jordan ×] [Medical Evacuation ×]
 *   No organization or contact matches Medical Evacuation in Jordan.
 *   Showing 3 offering Medical Evacuation outside Jordan, and 9 related to Jordan without it.
 *   Closest Medical Evacuation: Cairo Medics — Cairo, Egypt (~490 km)
 *   Try: [Any service in Jordan] [Medical Evacuation anywhere] [… in Middle East and North Africa]
 */
const SearchSummaryBanner: React.FC<SearchSummaryBannerProps> = ({ summary, compact = false, onPick, onOpen }) => {
    if (!summaryHasContent(summary)) return null;

    const { understood, headline, detail, counts } = summary;
    const noFullMatch = summary.structured && counts.full === 0;
    const alternatives = onPick ? (summary.alternatives ?? []) : [];
    const nearest = summary.nearest ?? [];
    const canRemove = !!onPick && understoodPartCount(summary) > 1;

    const removeButton = (part: QueryPart, label: string) => canRemove && (
        <button
            type="button"
            className={`btn-unstyled ${classes.chipRemove}`}
            title={`Search without ${label}`}
            aria-label={`Search without ${label}`}
            onClick={(event) => {
                event.stopPropagation();
                onPick!(removeQueryPart(summary, part));
            }}
        >
            ×
        </button>
    );

    return (
        <div className={`${classes.banner} ${compact ? classes.compact : ""}`} role="status">
            <div className={classes.understood}>
                <span className={classes.understoodLabel}>Understood</span>
                {understood.module && (
                    <span className={classes.chip} title={`Only ${understood.module.toLowerCase()}, from "${understood.moduleText}"`}>
                        <i className="bi bi-collection" />
                        {understood.module}
                        {removeButton("module", understood.module)}
                    </span>
                )}
                {understood.place && (
                    <span
                        className={classes.chip}
                        title={understood.placeText && understood.placeText !== understood.place
                            ? `Place, from "${understood.placeText}"`
                            : "Place"}
                    >
                        <i className="bi bi-geo-alt" />
                        {understood.place}
                        {removeButton("place", understood.place)}
                    </span>
                )}
                {understood.service && (
                    <span
                        className={classes.chip}
                        title={understood.serviceNames.length > 1
                            ? `Service, from "${understood.serviceText}": ${understood.serviceNames.join(", ")}`
                            : understood.serviceText && understood.serviceText !== understood.service
                                ? `Service, from "${understood.serviceText}"`
                                : "Service"}
                    >
                        <i className="bi bi-tools" />
                        {understood.service}
                        {removeButton("service", understood.service)}
                    </span>
                )}
                {understood.date && (
                    <span className={classes.chip} title={`Period, from "${understood.dateText}"`}>
                        <i className="bi bi-calendar-range" />
                        {understood.date}
                        {removeButton("date", understood.date)}
                    </span>
                )}
                {understood.text.length > 0 && (
                    <span className={`${classes.chip} ${classes.chipText}`} title="Matched against names, references, emails and phones">
                        <i className="bi bi-type" />
                        “{understood.text.join(" ")}”
                        {removeButton("text", `"${understood.text.join(" ")}"`)}
                    </span>
                )}
                {(understood.aliases ?? []).map((alias) => (
                    <span
                        key={`${alias.from}-${alias.to}`}
                        className={`${classes.chip} ${classes.chipText}`}
                        title="A search alias (Lookups → Search Aliases) rewrote this before searching"
                    >
                        <i className="bi bi-arrow-left-right" />
                        {alias.from} → {alias.to}
                    </span>
                ))}
            </div>
            {headline && (
                <div className={`${classes.headline} ${noFullMatch ? classes.headlineNoMatch : ""}`}>
                    {noFullMatch && <i className="bi bi-exclamation-circle" />}
                    {headline}
                </div>
            )}
            {detail && <div className={classes.detail}>{detail}</div>}
            {nearest.length > 0 && (
                <div className={classes.nearest}>
                    <span className={classes.rowLabel}>Closest {understood.service ?? "coverage"}</span>
                    {nearest.map((item) => {
                        const label = `${item.name} — ${item.place} (~${item.distanceKm} km)`;
                        return onOpen ? (
                            <button
                                key={item.organizationId}
                                type="button"
                                className={`btn-unstyled ${classes.nearestLink}`}
                                title="Open this provider"
                                onClick={() => onOpen(item.url)}
                            >
                                <i className="bi bi-geo" />
                                {label}
                            </button>
                        ) : (
                            <a
                                key={item.organizationId}
                                className={classes.nearestLink}
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open this provider"
                            >
                                <i className="bi bi-geo" />
                                {label}
                            </a>
                        );
                    })}
                </div>
            )}
            {alternatives.length > 0 && (
                <div className={classes.alternatives}>
                    <span className={classes.rowLabel}>Try</span>
                    {alternatives.map((alternative) => (
                        <button
                            key={alternative.text}
                            type="button"
                            className={`btn-unstyled ${classes.alternative}`}
                            title={`Search "${alternative.text}"`}
                            onClick={() => onPick!(alternative.text)}
                        >
                            {alternative.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchSummaryBanner;
